
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function ContactPage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: '',
    contact_method: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    // Schema.org JSON-LD for ContactPage
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "ご予約・お問い合わせ - 南森町鍼灸院",
      "description": "南森町鍼灸院へのご予約・お問い合わせはこちら。お電話またはお問い合わせフォームからお気軽にご連絡ください。",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      "mainEntity": {
        "@type": "MedicalBusiness",
        "name": "南森町鍼灸院",
        "telephone": "06-6949-8663",
        "email": "info@minamimori-acupuncture.com"
      }
    });
    document.head.appendChild(script);

    // Set page title and meta description
    document.title = "ご予約・お問い合わせ - 南森町鍼灸院";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '南森町鍼灸院へのご予約・お問い合わせはこちら。お電話またはお問い合わせフォームからお気軽にご連絡ください。');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d3jlk77uouli8ar1pcug',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formBody
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          subject: '',
          contact_method: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header
        className="shadow-sm border-b border-gray-100"
        style={{ backgroundColor: '#D8F25E' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center cursor-pointer">
              <div className="text-center">
                <h1
                  className="text-2xl md:text-4xl font-bold"
                  style={{
                    fontFamily:
                      '"Hiragino Sans", "Noto Sans JP", sans-serif',
                    color: '#526D1A'
                  }}
                >
                  南森町鍼灸院
                </h1>
                <p
                  className="mt-1 md:mt-2 text-sm md:text-lg opacity-90"
                  style={{ color: '#526D1A' }}
                >
                  女性・小児専門（乳幼児〜小・中学生）
                </p>
              </div>
              <div className="ml-2 md:ml-0 -mt-2 md:-mt-4">
                <img
                  src="https://static.readdy.ai/image/66b8c69278644cf9dd6637b765df0157/e2fcab9355782d68579ae8474689f655.webp"
                  alt="ハリネズミのマスコット"
                  className="w-16 h-16 md:w-28 md:h-28 object-contain"
                />
              </div>
            </Link>

            {/* デスクトップナビゲーション */}
            <nav className="hidden md:flex space-x-6 lg:space-x-8">
              <Link
                to="/"
                className="text-base lg:text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap"
                style={{ color: '#526D1A' }}
              >
                ホーム
              </Link>
              <Link
                to="/about"
                className="text-base lg:text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap"
                style={{ color: '#526D1A' }}
              >
                当院について
              </Link>
              <Link
                to="/menu"
                className="text-base lg:text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap"
                style={{ color: '#526D1A' }}
              >
                治療料金
              </Link>
              <Link
                to="/access"
                className="text-base lg:text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap"
                style={{ color: '#526D1A' }}
              >
                アクセス
              </Link>
              <Link
                to="/contact"
                className="text-base lg:text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer border-b-2 whitespace-nowrap"
                style={{ color: '#526D1A', borderColor: '#526D1A' }}
              >
                ご予約・お問い合わせ
              </Link>
            </nav>

            {/* モバイルメニューボタン */}
            <button
              className="md:hidden p-2 rounded-md cursor-pointer"
              style={{ color: '#526D1A' }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i
                className={`${
                  isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-2xl`}
              ></i>
            </button>
          </div>

          {/* モバイルナビゲーション */}
          {isMobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col space-y-3">
                <Link
                  to="/"
                  className="text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer py-2"
                  style={{ color: '#526D1A' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  ホーム
                </Link>
                <Link
                  to="/about"
                  className="text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer py-2"
                  style={{ color: '#526D1A' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  当院について
                </Link>
                <Link
                  to="/menu"
                  className="text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer py-2"
                  style={{ color: '#526D1A' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  治療料金
                </Link>
                <Link
                  to="/access"
                  className="text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer py-2"
                  style={{ color: '#526D1A' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  アクセス
                </Link>
                <Link
                  to="/contact"
                  className="text-lg font-medium hover:opacity-80 transition-opacity cursor-pointer py-2 border-b-2"
                  style={{ color: '#526D1A', borderColor: '#526D1A' }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  ご予約・お問い合わせ
                </Link>
              </div>
            </nav>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* ページタイトル */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#526D1A' }}>
            ご予約・お問い合わせ
          </h1>
          <p className="text-lg text-gray-600">お気軽にご相談ください</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* 予約ボタン */}
          <div
            className="bg-white rounded-lg shadow-lg p-8"
            style={{ backgroundColor: '#D8F25E' }}
          >
            <h2
              className="text-3xl font-bold mb-6 text-center"
              style={{ color: '#526D1A' }}
            >
              ご予約
            </h2>
            <p className="text-lg text-gray-700 mb-8 text-center">
              まずはお気軽にご予約ください。<br />初めての方にも安心して受けていただけるよう、丁寧にお話しを伺います。
            </p>

            <div className="text-center">
              <button
                className="bg-white text-white px-12 py-6 rounded-full text-2xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap transform hover:scale-105"
                style={{
                  backgroundColor: '#526D1A',
                  border: '3px solid #526D1A'
                }}
                onClick={() => {
                  window.open(
                    'https://kenkounihari.seirin.jp/clinic/18451/reserve',
                    '_blank'
                  );
                }}
              >
                <i className="ri-calendar-line mr-3"></i>
                今すぐ予約する
              </button>
            </div>

            <div className="mt-8 p-4 bg-white bg-opacity-70 rounded-lg">
              <h3 className="font-semibold mb-2" style={{ color: '#526D1A' }}>
                予約システムの特徴
              </h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>• 24時間いつでも予約可能</li>
                <li>• リアルタイムで空き状況を確認</li>
                <li>• 予約確認メールを自動送信</li>
                <li>• 予約変更・キャンセルも簡単</li>
              </ul>
            </div>

            {/* ハリネズミイラスト */}
            <div className="mt-12 text-center">
              <img
                src="https://static.readdy.ai/image/66b8c69278644cf9dd6637b765df0157/5cb7fd02ab9f609a2b6620a25b75d6df.webp"
                alt="ハリネズミのイラスト"
                className="w-80 h-80 object-contain mx-auto"
              />
            </div>
          </div>

          {/* お問い合わせフォーム */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2
              className="text-3xl font-bold mb-6 text-center"
              style={{ color: '#526D1A' }}
            >
              お問い合わせフォーム
            </h2>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg">
                <p className="text-green-700 text-center">
                  お問い合わせを送信いたしました。ありがとうございます。
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg">
                <p className="text-red-700 text-center">
                  送信に失敗しました。もう一度お試しください。
                </p>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="contact-form-sirochoco"
            >
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{ focusRingColor: '#526D1A' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{ focusRingColor: '#526D1A' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{ focusRingColor: '#526D1A' }}
                  />
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white cursor-pointer"
                    style={{ focusRingColor: '#526D1A' }}
                  >
                    <option value="">選択してください</option>
                    <option value="初回相談・カウンセリング希望">
                      初回相談・カウンセリング希望
                    </option>
                    <option value="治療内容について">治療内容について</option>
                    <option value="料金について">料金について</option>
                    <option value="予約の変更・キャンセル">
                      予約の変更・キャンセル
                    </option>
                    <option value="美容鍼について">美容鍼について</option>
                    <option value="その他のご質問">その他のご質問</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="contact_method"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    ご希望の連絡方法
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="contact_method"
                        value="お電話"
                        checked={formData.contact_method === 'お電話'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      お電話
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="contact_method"
                        value="メール"
                        checked={formData.contact_method === 'メール'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      メール
                    </label>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    詳細・ご質問
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none"
                    style={{ focusRingColor: '#526D1A' }}
                    placeholder="ご質問やご相談内容を詳しくお書きください（500文字以内）"
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500文字
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.name ||
                    !formData.phone ||
                    !formData.email ||
                    !formData.subject
                  }
                  className="w-full px-6 py-4 text-white rounded-md text-lg font-bold transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#526D1A' }}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-line animate-spin mr-2"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      送信する
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* 電話でのお問い合わせ */}
        <section className="mt-16">
          <div
            className="bg-white rounded-lg shadow-lg p-8 max-w-3xl mx-auto"
            style={{ backgroundColor: '#f8f9f0' }}
          >
            <h2
              className="text-3xl font-bold mb-6 text-center"
              style={{ color: '#526D1A' }}
            >
              お電話でのお問い合わせ
            </h2>

            <div className="text-center mb-6">
              <div className="flex justify-center items-center mb-4">
                <i
                  className="ri-phone-line text-5xl mr-4"
                  style={{ color: '#D8F25E' }}
                ></i>
                <div>
                  <a
                    href="tel:06-6949-8663"
                    className="text-3xl font-bold cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ color: '#526D1A' }}
                  >
                    06-6949-8663
                  </a>
                  <p className="text-lg text-gray-600">
                    受付時間：診療時間内
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg">
              <h3
                className="font-semibold mb-4"
                style={{ color: '#526D1A' }}
              >
                お電話でのお問い合わせについて
              </h3>
              <ul className="text-gray-700 space-y-2">
                <li>• 治療中は電話に出れない場合がございます</li>
                <li>
                  • 折り返しお電話いたしますので、お名前と連絡先をお願いします
                </li>
                <li>
                  • 予約の変更・キャンセルは前日の営業時間内までにお願いします
                </li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      {/* フッター */}
      <footer
        className="mt-16 py-8 text-center text-gray-600"
        style={{ backgroundColor: '#f8f9f0' }}
      >
        <div className="max-w-6xl mx-auto px-4">
          <p>&copy; 2025 南森町鍼灸院. All rights reserved.</p>
          <p className="mt-2">
            <a
              href="https://readdy.ai/?origin=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              Website Builder
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
